import React, { useEffect, useRef, useState } from 'react';
import i18n from 'utils/i18n';

import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import AnchorLink from 'components/atoms/anchor-link';
import Button from 'components/atoms/button';
import Card from 'components/molecules/card';
import CustomSelect from 'components/atoms/custom-select';
import FormRadioButton from 'components/atoms/form-radio-button';
import FormRadioGroup from 'components/atoms/form-radio-group';
import Modal from 'components/molecules/simple-modal';
import Title from 'components/atoms/title';

import { useServiceAppointment, ACTIONS } from 'providers/service-appointment';

import { dateIso2Br, extractCountryCodes, removePhoneCodes } from 'utils/functions';
import { formatPriceAppointment } from 'utils/functions/appointment';
import { defaultCountriesCodes } from 'utils/__data__/default-countries-codes';

import { IconCalendar, IconClock } from 'components/icons';

// loading the sass style fot the component
import css from './styles.module.scss';

/**
 * Molecule ServiceAppointmentDateAndPriceStep
 *
 * <!-- TODO: add a description here! -->
 */
function ServiceAppointmentDateAndPriceStep(props) {
  const t = i18n.useTranslations('components.service-appointment-date-and-price-step');
  const { className = '', onNextStep, locale, km, hasRecall, hasWash, userRegisterData = {} } = props;

  const validationSchema = yup
    .object()
    .shape({
      waitForVehicle: yup.string().oneOf(['true', 'false']),
      choiceDataAndTime: yup.string().oneOf(['true', 'false']),
    })
    .required();

  const {
    register,
    setValue,
    watch,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      choiceDataAndTime: 'true',
      waitForVehicle: 'true',
    }
  });

  const firstNewDateRef = useRef(null);
  const { state, dispatch } = useServiceAppointment();
  const { slots, selectedSlot } = state;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [listOfDates, setListOfDates] = useState([]);
  const [cardsData, setCardsData] = useState([]);
  const [isPrice, setIsPrice] = useState(null);
  const [priceWithoutTax, setPriceWithoutTax] = useState(null);
  const [legalText, setLegalText] = useState(null);
  const [hasNewDates, setHasNewDates] = useState(false);
  const [isSlotsError, setIsSlotsError] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [newSlots, setNewSlots] = useState([]);
  const waitForVehicle = watch('waitForVehicle');
  const [newDate, setNewDate] = useState('placeholder');
  const [newHourSlot, setNewHourSlot] = useState('placeholder');
  const isPriceValid = isPrice !== null || (isPrice !== null && newHourSlot !== 'placeholder');


  const isFormValid = newDate !== 'placeholder' && newHourSlot !== 'placeholder';

  useEffect(() => {
    setValue('waitForVehicle', state.selectedWaitForVehicle);
    setInitialDateAndTime();
    getCardsData();
    organizeSlotsByDate(slots);
  }, []);

  useEffect(() => {
    organizeSlotsByDate(slots);
  }, [hasNewDates, slots]);

  const setInitialDateAndTime = () => {
    if (selectedSlot) {
      const date = selectedSlot.openDate.split(' ')[0];
      const hour = selectedSlot.openDate;
      setNewDate(date);
      setNewHourSlot(hour);
      setIsPrice(formatPriceAppointment(selectedSlot.price, locale, t('bonus_text')));
      return;
    }

    if (slots.length > 0) {
      const currentDate = new Date();
      const sortedSlots = [...slots].sort((a, b) => new Date(a.openDate) - new Date(b.openDate));
      const nearestSlot = sortedSlots.find(slot => new Date(slot.openDate) >= currentDate) || sortedSlots[0];

      if (nearestSlot) {
        const date = nearestSlot.openDate.split(' ')[0];
        const hour = nearestSlot.openDate;
        setNewDate(date);
        setNewHourSlot(hour);
        setIsPrice(formatPriceAppointment(nearestSlot.price, locale, t('bonus_text')));
      }
    }
  };

  const fetchSlots = async () => {
    setLoadingSlots(true);
    setIsSlotsError(false);

    const cleanedKm = state.km.replace(/[^\d.]/g, '') || '';
    const codesList = extractCountryCodes(defaultCountriesCodes);
    const phoneWithoutCode = removePhoneCodes(userRegisterData.cellphoneNumber, codesList);
    const SLOTS_COUNT_INCREMENT = 1;

    const body = {
      customerVehicleId: state?.customerVehicleId,
      dealerId: state.dealerId,
      km: cleanedKm,
      hasRecall: state.hasRecall === 'true',
      hasWash: state.hasWash || false,
      additionalServices: state.additionalServices || [],
      others: state.others || '',
      stepCount: state.fetchSlotsCount + SLOTS_COUNT_INCREMENT,
      contact: {
        firstName: userRegisterData?.name,
        lastName: userRegisterData?.lastName,
        email: userRegisterData?.email,
        dni: userRegisterData?.dni,
        mobile: phoneWithoutCode
      }
    };

    try {
      const response = await fetch('/api/appointment/get-slots', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch slots: ${response?.status} - ${response?.statusText}`);
      }

      const responseData = await response.json();

      const fetchedSlots = Array.isArray(responseData) ? responseData : [];
      if (fetchedSlots.length === 0) {
        setIsSlotsError(true);
      } else {
        setNewSlots(fetchedSlots);
        const combinedSlots = [
          ...slots,
          ...fetchedSlots.filter(
            (newSlot) => !slots.some((oldSlot) => oldSlot.openDate === newSlot.openDate)
          ),
        ];

        dispatch({ type: ACTIONS.SET_SLOTS, payload: combinedSlots });
        dispatch({ type: ACTIONS.INCREMENT_FETCH_SLOTS_COUNT });
        setHasNewDates(true);
      }
    } catch (error) {
      console.error(error);
      setIsSlotsError(true);
      setHasNewDates(true);
    } finally {
      setLoadingSlots(false);
    }
  };

  const closeModal = () => setIsModalOpen(false);

  const organizeSlotsByDate = (slots) => {
    if (!slots?.length) {
      setListOfDates([]);
      setIsPrice(null);
      return;
    }
    const slotsByDate = slots?.reduce((acc, slot) => {
      const date = slot.openDate.split(' ')[0];
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(slot);
      return acc;
    }, {});
    const sortedDates = Object.keys(slotsByDate).sort((a, b) => a - b);
    setListOfDates(sortedDates);
  };

  const getCardsData = () => {
    const cards = [
      hasRecall === 'true' && {
        key: 'recall',
        title: t('recall_service_title'),
        content: t('recall_service_content')
      },
      km && {
        key: 'mantenimiento',
        title: `${t('maintenance_service_title')} ${km}`,
        content: t('maintenance_service_content')
      },
      hasWash && {
        key: 'wash',
        title: t('wash_service_title'),
        content: t('wash_service_content')
      }
    ]
      .filter(Boolean)
      .map((card) => ({
        ...card,
        variant: 'simple-card',
        isUppercase: false
      }));

    setCardsData(cards);
  };

  const onChangeDate = (e) => {
    setNewDate(e.target.value);
    setNewHourSlot('placeholder');
    setIsPrice(null);
  };

  const onChangeHour = (e) => {
    setIsPrice(null);
    const selectedHour = e.target.value;
    setNewHourSlot(selectedHour);
    const selected = slots.find((slot) => slot?.openDate === selectedHour);
    setIsPrice(formatPriceAppointment(selected?.price, locale, t('bonus_text')));
    setPriceWithoutTax(formatPriceAppointment(selected?.priceWithoutTax, locale, t('bonus_text')));
  };

  const handleConfirmation = () => {
    const formattedDateTime = `${newHourSlot}`;
    const selectedSlotAvailable = slots.find((slot) => slot.openDate === formattedDateTime);
    if (selectedSlotAvailable) {
      dispatch({ type: ACTIONS.SET_SELECTED_SLOT, payload: selectedSlotAvailable });
      dispatch({ type: ACTIONS.SET_SELECTED_WAIT_FOR_VEHICLE, payload: waitForVehicle });
      setIsPrice(null);
      onNextStep();
      return;
    }
  };

  const handleReturnToDealers = () => {
    const prevStepIndex = state.currentStep - 1;

    dispatch({ type: ACTIONS.SET_CURRENT_STEP, payload: prevStepIndex });
  };

  const getLegalText = (price) => {
    const isBonificado = price ? String(price).includes('Bonificado') : false;
    const part1 = isBonificado ? t('legal_text_zero_price_part1') : t('legal_text_standard_part1');
    const part2 = t('legal_text_part2');
    const maintenanceLinkLabel = t('maintenance_link_label');
    const maintenanceLinkUrl = t('maintenance_link_url');
    const maintenanceLinkTitle = t('maintenance_link_title');
    const maintenanceLinkAriaLabel = t('maintenance_link_arial_label');

    return (
      <span>
        {part1}
        <a href={maintenanceLinkUrl} aria-label={maintenanceLinkAriaLabel} title={maintenanceLinkTitle} target="_blank">
          {maintenanceLinkLabel}
        </a>
        {part2}
      </span>
    );
  };

  const displayModalWithLegalText = (price) => {
    setLegalText(getLegalText(price));
    setIsModalOpen(true);
  };

  const renderDateOption = ({ option, index, optionClass, handleOptionClick, handleOptionKeyDown, value }) => {
    if (option?.disabled) {
      return (
        <li
          key={`divider-${index}`}
          className={css['date-divider']}
          aria-hidden="true"
        >
          <hr className={css['divider']} />
        </li>
      );
    }

    const isFirstNewDate =
      option.className?.includes('new-date') &&
      index === oldDates.length + (oldDates.length > 0 ? 1 : 0);
    return (
      <li
        key={index}
        className={optionClass}
        onClick={() => handleOptionClick(option, index)}
        onKeyDown={(e) => handleOptionKeyDown(e, option, index)}
        role="option"
        aria-selected={option?.value === value}
        tabIndex={-1}
        ref={isFirstNewDate ? firstNewDateRef : null}
      >
        {option?.label}
      </li>
    );
  };


  const RenderEqualizer = () => (
    <div className={css["equalizer-circle"]}>
      <div className={css["bar"]}></div>
      <div className={css["bar"]}></div>
      <div className={css["bar"]}></div>
      <div className={css["bar"]}></div>
    </div>
  );

  const isNewDate = (date) => {
    if (!Array.isArray(newSlots)) {
      return false;
    }
    return newSlots.some((slot) => slot.openDate.split(' ')[0] === date);
  };

  const oldDates = listOfDates.filter((date) => !isNewDate(date));
  const newDates = listOfDates.filter((date) => isNewDate(date));

  return (
    <div className={`${css['molecule__service-appointment-date-and-price-step-container']} ${className}`}>
      <section className={css['section-choose-date']}>
        <div className={css['choose-date-header']}>
          <h3 className={css['header-title']}>{t('choice_date_title')}</h3>
          <p className={css['header-sub-title']}>{t('choice_date_sub_title')}</p>
        </div>
        <div
          className={`${css['form-container-selected-date-time']} ${loadingSlots ? css['initial-state'] : hasNewDates ? css['has-new-dates'] : ''
            }`}
        >
          <div className={css['selected-and-info']} >
            <div className={css['selected-date-time']}>
              <CustomSelect
                className={css['input-selected']}
                id="newDate"
                icon={<IconCalendar />}
                prefixText={t('prefix_new_date')}
                placeholder={t('placeholder_new_date')}
                value={newDate}
                onChange={onChangeDate}
                renderCustomOption={renderDateOption}
                onOpen={() => {
                  setTimeout(() => {
                    if (hasNewDates && firstNewDateRef.current) {
                      firstNewDateRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                    }
                  }, 0);
                }}
              >
                {oldDates.map((date, index) => (
                  <option className={css['old-date']} key={`old-${index}`} value={date}>
                    {dateIso2Br(date)}
                  </option>
                ))}
                {oldDates.length > 0 && newDates.length > 0 && (
                  <hr disabled className={css['date-divider']} key="divider" />
                )}
                {newDates.map((date, index) => (
                  <option className={css['new-date']} key={`new-${index}`} value={date}>
                    {dateIso2Br(date)}
                  </option>
                ))}
              </CustomSelect>

              <CustomSelect
                className={css['input-selected']}
                id="newHour"
                icon={<IconClock />}
                prefixText={t('prefix_new_hour')}
                placeholder={t('placeholder_new_hour')}
                value={newHourSlot}
                onChange={onChangeHour}
              >
                {slots
                  .filter((slot) => slot.openDate.split(' ')[0] === newDate)
                  .map((slot, index) => (
                    <option key={index} value={slot.openDate}>
                      {slot.openDate.split(' ')[1].slice(0, 5)}
                    </option>
                  ))}
              </CustomSelect>
            </div>

            {!loadingSlots && <div className={css['info-date-time']}>
              <span className={`${css['info-date-time-content']} ${hasNewDates ? css['has-new-dates'] : ''}`}>{hasNewDates ? t('new_dates_added') : t('info_first-slots')}</span>
            </div>}

          </div>
          <div className={css['slots-options']}>
            {isSlotsError ? (
              <div className={css['no-slots-message']}>
                <h4 className={css['no-slots-title']}>{t('no_slots_title')}</h4>
                <div className={css['no-slots-actions']}>
                  <Button
                    className={css['no-slots-change-dealer']}
                    onClick={handleReturnToDealers}
                    color="ghost-black"
                  >
                    {t('label_change_dealer')}
                  </Button>
                  <Button
                    className={css['no-slots-contact-point']}
                    onClick={() => window.location.href = t('contact_point_url')}
                  >
                    {t('label_contact_point')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className={css['more-options-link-loading']}>
                <div className={css['more-options-text-link']}>
                  <span className={css['more-options-text']}>{t('more_options_text')}</span>
                  <AnchorLink
                    className={css['more-slots-link']}
                    hasArrow={false}
                    linkLabel={t('label_more_slots')}
                    onClick={fetchSlots}
                    disabled={loadingSlots}
                  />
                </div>
                {loadingSlots && <RenderEqualizer />}
              </div>
            )}
          </div>
        </div>
      </section>
      <FormRadioGroup
        className={css['form-container-group-wait-for-vehicle']}
        label={t('label_radio_group_wait_for_vehicle')}
        name="waitForVehicle"
        dataRegister={register('waitForVehicle')}
        dataErrors={errors['waitForVehicle']}
        spacingGap="standard"
      >
        <FormRadioButton
          className={css['group-radio-button-vehicle']}
          id="radio-yes"
          label={t('label_select_wait_yes')}
          value="true"
        />
        <FormRadioButton
          className={css['group-radio-button-vehicle']}
          id="radio-no"
          label={t('label_select_wait_no')}
          value="false"
        />
      </FormRadioGroup>

      <div className={css['requested-services-and-price-container']}>
        {cardsData?.length > 0 && (
          <div className={css['requested-services-container']}>
            <h2 className={css['requested-services-title']}>{t('requested_services_title')}</h2>

            <div className={css['requested-services-info']}>
              {cardsData?.map((cardData) => (
                <div key={cardData?.key}>
                  <Card
                    className={css['card-services']}
                    title={cardData?.title}
                    content={cardData?.content}
                    variant={cardData?.variant}
                    isUppercase={cardData?.isUppercase}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
        {isPriceValid && (
          <div className={css['requested-price-container']}>
            <div className={css['requested-price-info']}>
              <span className={css['title-price-info']}>{t('title_price_info')}</span>
              <span className={css['price-info']}>{isPrice}</span>
            </div>
            {!isPrice.includes(t('bonus_text')) && priceWithoutTax && (
              <div className={css['requested-price-info']}>
                <span className={css['title-price_without_tax-info']}>{t('title_price_without_tax_info')}</span>
                <span className={css['price_without_tax-info']}>{priceWithoutTax}</span>
              </div>
            )}
            <AnchorLink
              linkLabel={t('label_legales')}
              onClick={() => displayModalWithLegalText(isPrice)}
            />
          </div>
        )}
      </div>

      <Button
        className={css['form-container-button']}
        onClick={() => handleConfirmation()}
        disabled={!isFormValid}
      >
        {t('label_button_confirm')}
      </Button>

      {
        isModalOpen && legalText && (
          <Modal className={css['modal-container']} isOpen={isModalOpen} onClose={closeModal}>
            <Title variant={2}>{t('title_modal_legal_text')}</Title>
            <div className={css['modal-container-content']}>{legalText}</div>
          </Modal>
        )
      }
    </div >
  );
}

export default ServiceAppointmentDateAndPriceStep;
